<script setup lang="ts">
import { computed, ref } from "vue";
import MagnifierIcon from "@magnit/icons/src/assets/icons/magnifier-24.svg";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VInlineButton from "@magnit/core/src/components/VInlineButton/VInlineButton.vue";
import VInput from "@magnit/core/src/components/VInput/VInput.vue";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import { responsiveClass } from "@magnit/core/src/utilities/helpers";
import type {
  ISearchEmits,
  ISearchExpose,
  ISearchProps,
} from "./VSearch.types";

const props = withDefaults(defineProps<ISearchProps>(), {
  placeholder: "",
  submitText: "Найти",
  submitVisible: true,
  cancelText: "Отмена",
  cancelVisible: true,
});
const emit = defineEmits<ISearchEmits>();

const inputRef = ref<InstanceType<typeof VInput> | null>(null);

const hasFocus = ref(false);

const submitClass = computed(() => {
  return [...responsiveClass("pl-search-submit_visible", props.submitVisible)];
});

const onInput = (e: Event) => {
  const target = e.target as HTMLInputElement;

  emit("update:modelValue", target.value);
};

const onCancel = () => {
  inputRef.value?.blur();
  emit("cancel");
};

const onClear = () => {
  emit("clear");
};

const onFocus = () => {
  hasFocus.value = true;
  emit("focus");
};

const onBlur = () => {
  // @NOTE: хак для того, чтобы при пустом значении поля
  // можно было кликнуть по кнопкам рядом с инпутом
  setTimeout(() => {
    hasFocus.value = false;
    emit("blur");
  }, 100);
};

const focusInput = () => {
  inputRef.value?.focus();
};

const blurInput = () => {
  inputRef.value?.blur();
};

defineExpose<ISearchExpose>({
  focus: focusInput,
  blur: blurInput,
});
</script>

<template>
  <form class="pl-search" @submit.prevent="emit('submit', modelValue)">
    <div class="pl-search-input">
      <VInput
        ref="inputRef"
        :model-value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :data-test-id="inputTestId"
        @focus="onFocus"
        @blur="onBlur"
        @input="onInput"
        @clear="onClear"
        @keyup.esc="blurInput"
        @update:model-value="(e) => emit('update:modelValue', e)"
      >
        <template #iconLeft>
          <MagnifierIcon v-if="!hasFocus" class="pl-search-icon" />
        </template>
      </VInput>
    </div>
    <div class="pl-search-buttons">
      <VButton
        v-if="submitVisible"
        class="pl-search-submit"
        :class="submitClass"
        type="submit"
        :data-test-id="buttonTestId"
      >
        {{ submitText }}
      </VButton>
      <VHidden v-if="cancelVisible" :hide="{ xs: false, l: true }">
        <VInlineButton
          v-if="hasFocus"
          class="pl-search-cancel"
          theme="secondary"
          @click="onCancel"
        >
          {{ cancelText }}
        </VInlineButton>
      </VHidden>
    </div>
  </form>
</template>

<style lang="postcss" scoped>
.pl-search {
  display: flex;
  width: 100%;

  .pl-search-cancel {
    margin-left: 12px;
  }

  .pl-search-submit {
    margin-left: 8px;
    display: none;

    &_visible {
      display: inline-block;
    }
  }
}

.pl-search-input {
  flex-grow: 1;
}

.pl-search-icon {
  color: var(--pl-icon-secondary);
}

.pl-search-buttons {
  display: flex;
  align-items: center;
}

@media (--pl-viewport-from-s) {
  .pl-search {
    .pl-search-submit {
      &_visible-false-s {
        display: none;
      }

      &_visible-true-s {
        display: inline-block;
      }
    }
  }
}

@media (--pl-viewport-from-m) {
  .pl-search {
    .pl-search-submit {
      &_visible-false-m {
        display: none;
      }

      &_visible-true-m {
        display: inline-block;
      }
    }
  }
}

@media (--pl-viewport-from-ml) {
  .pl-search {
    .pl-search-submit {
      &_visible-false-ml {
        display: none;
      }

      &_visible-true-ml {
        display: inline-block;
      }
    }
  }
}

@media (--pl-viewport-from-l) {
  .pl-search {
    .pl-search-submit {
      &_visible-false-l {
        display: none;
      }

      &_visible-true-l {
        display: inline-block;
      }
    }
  }
}

@media (--pl-viewport-from-xl) {
  .pl-search-submit {
    &_visible-false-xl {
      display: none;
    }

    &_visible-true-xl {
      display: inline-block;
    }
  }
}
</style>
